import { getPref, setPref, subscribePref } from "./userPrefs";
import {
  GDL_DEFAULT_VIEW_SCOPE,
  viewScopeOf,
} from "./guestDetailLayoutPref";

/**
 * Per-user ENTITY-popup layout choice (speaker / sponsor), scoped per
 * (entity, event, view-context). Twin of `guestDetailLayoutPref` — the guest
 * module stays untouched; only its two entity-free helpers are reused.
 *
 * Key shape: `edl-<entity>-<eventId>-<viewId|DEFAULT>`. The view scope makes a
 * manual pick an override *of the current view context* rather than a global
 * sticky pref: picking layout X while view A (linked to layout Y) is active
 * must visibly win, switching to view B must restore B's own link, and a pick
 * made months ago must not shadow every future link. Surfaces with no view
 * (the builder tab, standalone popups) resolve through the DEFAULT scope.
 *
 * `userPrefs` is NOT same-tab reactive, but the picker, the popup and the
 * builder must all re-render on selection — hence the listener set below,
 * shaped for `useSyncExternalStore`.
 */

/** Sentinel id for the unnamed Standard layout. Reserved server-side. */
export const EDL_STANDARD = "default";

export { GDL_DEFAULT_VIEW_SCOPE as EDL_DEFAULT_VIEW_SCOPE, viewScopeOf };

function prefKey(entity: string, eventId: string, viewScope: string): string {
  return `edl-${entity}-${eventId}-${viewScope}`;
}

type Listener = () => void;
const listeners = new Set<Listener>();

/** Selected layout id for this (entity, event, view-context), or null. */
export function getEntityLayoutPref(
  entity: string,
  eventId: string,
  viewScope: string,
): string | null {
  return getPref(prefKey(entity, eventId, viewScope));
}

/** Persist a pick (EDL_STANDARD or a named id) and notify THIS tab. */
export function setEntityLayoutPref(
  entity: string,
  eventId: string,
  viewScope: string,
  layoutId: string,
): void {
  setPref(prefKey(entity, eventId, viewScope), layoutId);
  for (const l of listeners) l();
}

/** Subscribe to picks — same-tab via the local set, cross-tab via userPrefs. */
export function subscribeEntityLayoutPref(
  entity: string,
  eventId: string,
  viewScope: string,
  onChange: () => void,
): () => void {
  listeners.add(onChange);
  const unsubCrossTab = subscribePref(
    prefKey(entity, eventId, viewScope),
    onChange,
  );
  return () => {
    listeners.delete(onChange);
    unsubCrossTab();
  };
}
